import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PackagePlus, Plus, Trash2, Loader2 } from "lucide-react";

const num = (v) => Number(String(v).replace(/\s/g, "").replace(",", "."));
const blank = (annee, fam) => ({ date: `${annee}-01-01`, designation: "", famille: fam || "", quantite: "", montant: "", ref: "" });

const Ligne = ({ r, i, fams, set, del }) => (
  <tr className="border-b last:border-0" data-testid={`entree-mp-row-${i}`}>
    <td className="py-1 pr-1"><Input type="date" className="h-8" value={r.date} onChange={(e) => set(i, "date", e.target.value)} data-testid={`entree-mp-date-${i}`} /></td>
    <td className="pr-1"><Input className="h-8" value={r.designation} onChange={(e) => set(i, "designation", e.target.value)} data-testid={`entree-mp-designation-${i}`} /></td>
    <td className="pr-1">
      <select className="h-8 w-full rounded-md border bg-background px-2 text-sm" value={r.famille} onChange={(e) => set(i, "famille", e.target.value)} data-testid={`entree-mp-famille-${i}`}>
        {fams.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>
    </td>
    <td className="pr-1"><Input className="h-8 text-right" inputMode="decimal" value={r.quantite} onChange={(e) => set(i, "quantite", e.target.value)} data-testid={`entree-mp-quantite-${i}`} /></td>
    <td className="pr-1"><Input className="h-8 text-right" inputMode="decimal" value={r.montant} onChange={(e) => set(i, "montant", e.target.value)} data-testid={`entree-mp-montant-${i}`} /></td>
    <td className="text-right text-xs tabular whitespace-nowrap pr-1">{num(r.quantite) > 0 ? (num(r.montant) / num(r.quantite)).toFixed(2) : "—"}</td>
    <td><Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => del(i)} data-testid={`entree-mp-delete-${i}`}><Trash2 className="h-4 w-4" /></Button></td>
  </tr>
);

export const EntreesMPDialog = ({ sid, annee, entrees, onSaved, unite = "kg" }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [fams, setFams] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows((entrees || []).map((e) => ({ ...e, ref: e.ref || "" })));
    api.get("/etat-stock/familles", { params: { societe_id: sid } }).then((r) => setFams(r.data));
  }, [open, sid, entrees]);

  const set = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const del = (i) => setRows((rs) => rs.filter((_, j) => j !== i));
  const save = async () => {
    setSaving(true);
    try {
      const payload = rows.map((r) => ({ ...r, quantite: num(r.quantite), montant: num(r.montant) }));
      await api.put("/etat-stock/entrees", { societe_id: sid, annee: Number(annee), entrees: payload });
      toast.success("Entrées de matières premières enregistrées");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)} data-testid="entrees-mp-open-btn"><PackagePlus className="h-4 w-4" /> Entrées MP</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="entrees-mp-dialog">
          <DialogHeader>
            <DialogTitle>Entrées de matières premières importées — {annee}</DialogTitle>
            <DialogDescription>Le prix par {unite} est calculé à partir du montant ; le stock et les coûts sont recalculés à l'enregistrement.</DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead><tr className="text-left text-muted-foreground border-b"><th className="py-2 w-36">Date</th><th>Désignation</th><th className="w-52">Famille (stock)</th><th className="w-28 text-right">Quantité ({unite})</th><th className="w-36 text-right">Montant (DA)</th><th className="w-20 text-right">PU / {unite}</th><th className="w-10" /></tr></thead>
              <tbody>{rows.map((r, i) => <Ligne key={i} r={r} i={i} fams={fams} set={set} del={del} />)}</tbody>
            </table>
          </div>
          <Button variant="outline" size="sm" className="gap-1 w-fit" onClick={() => setRows((rs) => [...rs, blank(annee, fams[0])])} data-testid="entree-mp-add-btn"><Plus className="h-4 w-4" /> Ajouter une entrée</Button>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="entrees-mp-cancel-btn">Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="entrees-mp-save-btn">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
